import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { io } from "socket.io-client";
import { fetchQrExpiryMs } from "../api/kioskAdmin";
import ShopLeaveModal from "../shop/ShopLeaveModal";
import ShopNoticeModal from "../shop/ShopNoticeModal";
import ShopStage from "../shop/ShopStage";
import { MOSIDA_API_BASE_URL, MOSIDA_API_ORIGIN, PAYMENT_SECRET_KEY } from "../shop/mosidaApi";
import {
  isPaymentSocketSuccess,
  readPaymentSession,
  savePendingPaymentOrderFromSocket,
} from "../shop/pendingPaymentOrder";
import {
  readShopCart,
  readShopPayment,
  saveShopCart,
  saveShopPayment,
} from "../shop/shopSession";
import { cartTotal, formatPrice, uniqueCartArtUrls } from "../shop/shopUtils";
import "../shop/shop.css";
import "../shop/shopKiosk.css";

const SUCCESS_NOTICE_MS = 3000;

function eventMatchesTempId(data, tempDataId) {
  if (!tempDataId || !data || typeof data !== "object") return false;
  const candidates = [
    data.temp_data_id,
    data.tempDataId,
    data.userId,
    data.user_id,
    data.id,
    data?.data?.temp_data_id,
    data?.data?.tempDataId,
    data?.data?.userId,
    data?.data?.id,
  ];
  return candidates.some((value) => String(value || "").trim() === tempDataId);
}

function QrPayment() {
  const navigate = useNavigate();
  const payment = readShopPayment();
  const cartItems = useMemo(() => readShopCart(), []);
  const tempDataId = String(
    payment?.tempDataId || readPaymentSession().tempDataId || ""
  ).trim();
  const paymentQR = payment?.qr;
  const total =
    typeof payment?.totalAmount === "number"
      ? payment.totalAmount
      : cartTotal(cartItems);

  const [expiryMs, setExpiryMs] = useState(null);
  const [remainingMs, setRemainingMs] = useState(0);
  const [expired, setExpired] = useState(false);
  const [qrLoaded, setQrLoaded] = useState(false);
  const [paid, setPaid] = useState(false);
  const [leaveOpen, setLeaveOpen] = useState(false);
  const handledRef = useRef(false);
  const successNavRef = useRef(null);
  const socketRef = useRef(null);
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  const countdown = useMemo(() => {
    const seconds = Math.max(0, Math.ceil(remainingMs / 1000));
    const minutes = Math.floor(seconds / 60);
    const rest = seconds % 60;
    return `${String(minutes).padStart(2, "0")}:${String(rest).padStart(2, "0")}`;
  }, [remainingMs]);

  const openBackModal = () => {
    if (paid) return;
    setLeaveOpen(true);
  };

  const goProduct = () => {
    setLeaveOpen(false);
    navigate("/product");
  };

  const finishPaid = (data) => {
    if (handledRef.current) return;
    handledRef.current = true;
    const payload = data && typeof data === "object" ? data : {};
    const session = savePendingPaymentOrderFromSocket(payload, tempDataId);
    const orderNumbers =
      session.orderNumbers?.length > 0
        ? session.orderNumbers
        : session.orderNumber
          ? [session.orderNumber]
          : [];
    const artUrls = uniqueCartArtUrls(readShopCart());
    saveShopCart([]);
    saveShopPayment({});
    successNavRef.current = {
      orderNumber: session.orderNumber || orderNumbers[0],
      orderNumbers,
      artUrls,
      paymentMethod: "qr",
    };
    setLeaveOpen(false);
    setPaid(true);
  };

  useEffect(() => {
    let cancelled = false;
    fetchQrExpiryMs().then((ms) => {
      if (!cancelled) setExpiryMs(ms);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!paymentQR) {
      setQrLoaded(false);
      return undefined;
    }
    setQrLoaded(false);
    const image = new Image();
    image.onload = () => setQrLoaded(true);
    image.onerror = () => setQrLoaded(true);
    image.src = paymentQR;
    return () => {
      image.onload = null;
      image.onerror = null;
    };
  }, [paymentQR]);

  useEffect(() => {
    if (!paymentQR || !expiryMs || !qrLoaded) return undefined;
    setExpired(false);
    const endsAt = Date.now() + expiryMs;
    const tick = () => {
      const left = Math.max(0, endsAt - Date.now());
      setRemainingMs(left);
      if (left <= 0) setExpired(true);
    };
    tick();
    const id = window.setInterval(tick, 250);
    return () => window.clearInterval(id);
  }, [paymentQR, expiryMs, qrLoaded]);

  // Giftshop-aligned payment socket: registerUser(tempDataId) + listen for success.
  useEffect(() => {
    if (!tempDataId) {
      console.error("[qr-payment] tempDataId is missing — cannot listen for payment");
      return undefined;
    }
    if (!PAYMENT_SECRET_KEY) {
      console.error(
        "[qr-payment] PAYMENT_SECRET_KEY missing. Set REACT_APP_PAYMENT_SECRET_KEY or REACT_APP_KIOSK_SECRET_KEY in .env and restart."
      );
      return undefined;
    }

    handledRef.current = false;

    const socket = io(`${MOSIDA_API_ORIGIN}/ws/payment`, {
      query: { secretKey: PAYMENT_SECRET_KEY },
      transports: ["websocket"],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: 5,
      timeout: 20000,
      forceNew: true,
      upgrade: false,
      auth: { secretKey: PAYMENT_SECRET_KEY },
    });
    socketRef.current = socket;

    const handlePaymentUpdate = (data) => {
      console.log("[qr-payment] payment update:", data);
      if (!data) return;
      // Giftshop: data.success. Also accept broader success shapes / order ids.
      if (data.success === true || data.success === "true" || isPaymentSocketSuccess(data)) {
        finishPaid(data);
      }
    };

    const onConnect = () => {
      console.log("[qr-payment] payment socket connected:", socket.id);
      socket.emit("registerUser", { userId: tempDataId });
    };

    socket.on("connect", onConnect);
    socket.on("connect_error", (error) => {
      console.error("[qr-payment] payment socket connect_error:", error);
    });
    socket.on("disconnect", (reason) => {
      console.warn("[qr-payment] payment socket disconnect:", reason);
    });
    // Backend may emit with temp_data_id as the event name.
    socket.on(tempDataId, handlePaymentUpdate);
    socket.on("paymentStatusUpdate", (data) => {
      if (!data) return;
      if (
        eventMatchesTempId(data, tempDataId) ||
        String(data.temp_data_id || "") === tempDataId ||
        String(data.id || "") === tempDataId ||
        String(data.userId || "") === tempDataId
      ) {
        handlePaymentUpdate(data);
      }
    });

    if (socket.connected) onConnect();

    return () => {
      socket.off("connect", onConnect);
      socket.off(tempDataId, handlePaymentUpdate);
      socket.off("paymentStatusUpdate");
      socket.off("connect_error");
      socket.off("disconnect");
      socket.disconnect();
      if (socketRef.current === socket) socketRef.current = null;
    };
    // finishPaid closes over tempDataId; effect already keyed on it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tempDataId]);

  // Fallback: when mobile success deletes temp-data, treat as paid (mosida-web pattern).
  useEffect(() => {
    if (!tempDataId || paid) return undefined;

    let cancelled = false;
    let sawTempData = false;

    const checkTempData = async () => {
      if (cancelled || handledRef.current) return;
      try {
        const response = await fetch(
          `${MOSIDA_API_BASE_URL}/user/payment/temp-data/${tempDataId}`,
          { cache: "no-store" }
        );
        if (response.ok) {
          sawTempData = true;
          return;
        }
        if (sawTempData && (response.status === 404 || response.status === 410)) {
          finishPaid({ success: true, temp_data_id: tempDataId });
        }
      } catch (error) {
        console.error("[qr-payment] temp-data poll error:", error);
      }
    };

    const bootstrap = async () => {
      try {
        const response = await fetch(
          `${MOSIDA_API_BASE_URL}/user/payment/temp-data/${tempDataId}`,
          { cache: "no-store" }
        );
        if (response.ok) sawTempData = true;
      } catch (error) {
        console.error("[qr-payment] temp-data bootstrap error:", error);
      }
    };

    bootstrap();
    const intervalId = window.setInterval(checkTempData, 2500);
    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tempDataId, paid]);

  useEffect(() => {
    if (!paid) return undefined;
    const timer = window.setTimeout(() => {
      navigateRef.current("/counter-payment", {
        replace: true,
        state: successNavRef.current || { paymentMethod: "qr" },
      });
    }, SUCCESS_NOTICE_MS);
    return () => window.clearTimeout(timer);
  }, [paid]);

  return (
    <ShopStage>
      <div className="shopScreen shopScreen--kiosk shopPay">
        <header className="galleryPage__header shopKioskHeader">
          <button
            type="button"
            className="galleryPage__back"
            onClick={openBackModal}
            disabled={paid}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="m12 19-7-7 7-7" />
              <path d="M19 12H5" />
            </svg>
            Back
          </button>
        </header>

        <div className="qrPay qrPay--card">
          <h1 className="qrPay__title">
            Scan QR <span>to Pay</span>
          </h1>
          <p className="qrPay__hint">Use your phone to scan the QR</p>
          {expired ? (
            <p className="shopError">
              This payment QR has expired. Go back and try again.
            </p>
          ) : !paymentQR ? (
            <div
              className="qrPay__frame qrPay__frame--loading"
              role="status"
              aria-live="polite"
            >
              <span className="galleryPage__spinner" aria-hidden="true" />
              <p className="qrPay__loadingText">Loading QR...</p>
            </div>
          ) : (
            <div className="qrPay__frame">
              {!qrLoaded ? (
                <div
                  className="qrPay__frame qrPay__frame--loading"
                  style={{
                    position: "absolute",
                    inset: 0,
                    width: "100%",
                    height: "100%",
                    boxShadow: "none",
                  }}
                  aria-hidden="true"
                >
                  <span className="galleryPage__spinner" />
                  <p className="qrPay__loadingText">Loading QR...</p>
                </div>
              ) : null}
              <img
                src={paymentQR}
                alt="Payment QR code"
                className="qrPay__image"
                style={{ opacity: qrLoaded ? 1 : 0 }}
              />
              {qrLoaded ? (
                <span className="qrPay__badge" aria-hidden="true">
                  MOSIDA
                </span>
              ) : null}
            </div>
          )}
          <p className="qrPay__amount">{formatPrice(total)}</p>
          <p className="qrPay__payable">Amount Payable</p>
          {!expired && paymentQR && expiryMs && qrLoaded ? (
            <p className="qrPay__timer">Expires in {countdown}</p>
          ) : null}
        </div>

        <ShopLeaveModal
          show={leaveOpen}
          intent="back"
          title="GO BACK TO THE PRINT SHOP?"
          copy="YOUR CART ITEMS WILL STAY SAVED. YOU CAN RETURN AND TRY PAYMENT AGAIN."
          onStay={() => setLeaveOpen(false)}
          onConfirm={goProduct}
        />

        <ShopNoticeModal
          show={paid}
          variant="cart"
          message="PAYMENT SUCCESSFULLY!"
          duration={SUCCESS_NOTICE_MS}
          dismissible={false}
        />
      </div>
    </ShopStage>
  );
}

export default QrPayment;
